import type { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type {
  ProfileFormInput,
  ProfileFormValues,
} from '@eliadmin/core/src/components/dlz/account/profile/profile-schema';
import {
  formatPhone,
  formatPostcode,
  normalizePhone,
  splitStreet,
} from '@eliadmin/core/src/components/dlz/auth/register/register-values';

type CustomerUpdate = Parameters<typeof updateCustomer>[0]['data'];

/** „Hlavná“ + „24“ → „Hlavná 24“: the design has one „Ulica a číslo domu“ field. */
function joinStreet(street: string | undefined, number: string | undefined) {
  return [street, number]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
}

function hasDeliveryAddress(customer: Customer) {
  return !!(customer.address_delivery || customer.city_delivery || customer.postcode_delivery);
}

/** The form as the customer record has it; without a stored delivery address the „same“ box is ticked. */
export function toProfileFormValues(customer: Customer, defaultCountry: string): ProfileFormInput {
  const country = customer.country?.id ? String(customer.country.id) : defaultCountry;

  return {
    name: customer.name ?? '',
    surname: customer.surname ?? '',
    email: customer.email ?? '',
    phone: formatPhone(customer.phone ?? ''),
    company: customer.company ?? '',
    ico: customer.ico ?? '',
    dic: customer.dic ?? '',
    ic_dph: customer.ic_dph ?? '',
    address: joinStreet(customer.address, customer.street_number),
    city: customer.city ?? '',
    postcode: formatPostcode(customer.postcode, country),
    country,
    same_delivery: !hasDeliveryAddress(customer),
    delivery_address: joinStreet(customer.address_delivery, customer.street_number_delivery),
    delivery_city: customer.city_delivery ?? '',
    delivery_postcode: formatPostcode(
      customer.postcode_delivery,
      customer.country_delivery?.id ?? country,
    ),
    delivery_country: customer.country_delivery?.id ?? country,
  };
}

/** „Rovnaká ako fakturačná“ clears the stored delivery address, so the checkout stops offering it. */
function toDeliveryUpdate(values: ProfileFormValues): CustomerUpdate {
  if (values.same_delivery) {
    return {
      address_delivery: '',
      street_number_delivery: '',
      city_delivery: '',
      postcode_delivery: '',
    };
  }

  const { street, number } = splitStreet(values.delivery_address);
  return {
    address_delivery: street,
    street_number_delivery: number,
    city_delivery: values.delivery_city,
    postcode_delivery: values.delivery_postcode,
    country_delivery: { id: values.delivery_country },
  };
}

/**
 * The customer fields the checkout prefills from (core `cart-order-form`) and the registration
 * writes: street and house number apart, the phone in E.164, the company identification only for a
 * company customer.
 */
export function toCustomerUpdate(
  values: ProfileFormValues,
  countries: Country[],
  isCompany: boolean,
): CustomerUpdate {
  const { street, number } = splitStreet(values.address);

  return {
    name: values.name,
    surname: values.surname,
    // Unchanged (the field is read-only) – sent like the core profile form does.
    email: values.email,
    phone: normalizePhone(values.phone),
    address: street,
    street_number: number,
    city: values.city,
    postcode: values.postcode,
    country: {
      id: values.country,
      name: countries.find(country => country.id === values.country)?.name ?? values.country,
    },
    ...toDeliveryUpdate(values),
    ...(isCompany
      ? { company: values.company, ico: values.ico, dic: values.dic, ic_dph: values.ic_dph }
      : {}),
  };
}

/** Fields of a 422 from `updateCustomer` (the customer's names) → fields of the form. */
export const SERVER_FIELD_MAP: Record<string, keyof ProfileFormValues> = {
  name: 'name',
  surname: 'surname',
  phone: 'phone',
  company: 'company',
  ico: 'ico',
  dic: 'dic',
  ic_dph: 'ic_dph',
  address: 'address',
  street_number: 'address',
  city: 'city',
  postcode: 'postcode',
  country: 'country',
  'country.id': 'country',
  address_delivery: 'delivery_address',
  street_number_delivery: 'delivery_address',
  city_delivery: 'delivery_city',
  postcode_delivery: 'delivery_postcode',
  country_delivery: 'delivery_country',
  'country_delivery.id': 'delivery_country',
};
